import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

/**
 * How a ticket's priority and stage are drawn, wherever they are drawn.
 *
 * These lived in `Inbox.tsx` while the thread header was the only thing rendering them. Moving
 * that metadata into the Details panel gave them a second consumer, and importing them back out
 * of `Inbox.tsx` would have closed a cycle — the Inbox renders the panel, so the panel cannot
 * sensibly import the Inbox. ES modules tolerate that; it is still the wrong shape.
 *
 * Keeping them together also keeps the rule together: a stage's *label and colour* are
 * per-workspace data, while the four stages themselves never are. Anything printing
 * `ticket.status` directly quietly ignores a workspace that renamed "Pending" to "In progress".
 */

/** How many of the three bars are filled — normal and low were previously the same grey pill
 *  with no way to tell them apart at a glance; the bar count now carries that distinction. */
const PRIORITY_BARS: Record<'low' | 'normal' | 'high' | 'urgent', number> = {
  low: 1,
  normal: 2,
  high: 3,
  urgent: 3,
}

/** A small ascending bar-chart glyph, the same shape convention this category's other tools use
 *  for priority — three bars, the shorter ones dimmed once the count exceeds them. Urgent adds a
 *  filled dot above the bars rather than a fourth bar, since it is a different kind of signal
 *  (drop everything) and not simply "more of the same."*/
export function PriorityIcon({ priority }: { priority: 'low' | 'normal' | 'high' | 'urgent' }) {
  const filled = PRIORITY_BARS[priority]
  const heights = [5, 8, 11]
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ flexShrink: 0 }}>
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * 5}
          y={12 - h}
          width="3"
          height={h}
          rx="1"
          fill="currentColor"
          opacity={i < filled ? 1 : 0.25}
        />
      ))}
      {priority === 'urgent' && <circle cx="12.5" cy="1.5" r="1.5" fill="currentColor" />}
    </svg>
  )
}

/** Plain-English fallback, shown only for the instant before the workspace's own labels have
 *  loaded — every workspace has real rows for all four kinds from the moment its database is
 *  provisioned, so this is never what a user actually sees in steady state. */
const DEFAULT_STATUS_META: Record<'open' | 'pending' | 'resolved' | 'closed', { label: string; color: string }> = {
  open: { label: 'Open', color: 'var(--cad-bad)' },
  pending: { label: 'Pending', color: 'var(--cad-warn)' },
  resolved: { label: 'Resolved', color: 'var(--cad-good)' },
  closed: { label: 'Closed', color: 'var(--cad-ink-muted)' },
}

/** The word and color for each stage are per-workspace (see TicketStatusSettings) — the stage
 *  itself never is, so this hook is the one place that reconciles "always exactly these four
 *  keys" with "the label on each one is data, not a constant." */
export function useStatusMeta(): Record<'open' | 'pending' | 'resolved' | 'closed', { label: string; color: string }> {
  const query = useQuery({ queryKey: ['ticket-statuses'], queryFn: api.ticketStatuses, staleTime: 5 * 60_000 })
  if (!query.data) return DEFAULT_STATUS_META
  const meta = { ...DEFAULT_STATUS_META }
  for (const status of query.data.statuses) meta[status.kind] = { label: status.label, color: status.color }
  return meta
}

export function TicketStatusDot({ status }: { status: 'open' | 'pending' | 'resolved' | 'closed' }) {
  const meta = useStatusMeta()[status]
  return (
    <span className="row gap-1" style={{ alignItems: 'center', color: meta.color }}>
      <span className="cad-dot" />
      <span style={{ color: 'var(--cad-ink)' }}>{meta.label}</span>
    </span>
  )
}
